<template>
  <div class="accordions-container is-hidden-tablet">
    <Accordion
      v-if="brandDescription"
      class="about-the-brand-accordion"
      :initial-expanded="false"
      :has-border="true"
      :has-toggle-icon="true"
    >
      <h4 class="title">
        {{ $t('brands.aboutTheBrand') }}
      </h4>
      <template #toggle-icon="slotProps">
        <Icons
          class="arrow-icon"
          :class="slotProps.toggledClass"
          name="arrow"
          :rotate="270"
          style="--icon-size: 15px"
        />
      </template>
      <template #content>
        <div class="content">
          {{ brandDescription }}
        </div>
      </template>
    </Accordion>
    <Accordion
      v-show="categoryFilters.length > 0"
      class="categories-accordion"
      :initial-expanded="false"
      :has-border="true"
      :has-toggle-icon="true"
    >
      <h4 class="title">
        {{ categoryTitle }}
      </h4>
      <template #toggle-icon="slotProps">
        <Icons
          :class="slotProps.toggledClass"
          :rotate="270"
          class="arrow-icon"
          name="arrow"
          style="--icon-size: 15px"
        />
      </template>
      <template #content>
        <div class="content">
          <SmartLink
            v-for="category in subCategories"
            :key="category.id"
            :native="true"
            :rel="category.label"
            :to="getCategorySlugUrl(category.slugUrl)"
            class="category-link"
          >
            {{ category.label }}
          </SmartLink>
          <SmartLink
            v-if="parentCategory"
            :native="true"
            :to="getCategorySlugUrl(parentCategory.slugUrl)"
            :rel="parentCategory.label"
            class="category-parent-link"
          >
            {{ $t('category.backTo', { category: parentCategory.label }) }}
          </SmartLink>
        </div>
      </template>
    </Accordion>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import pluralize from 'pluralize'
import Accordion from '@sephora-asia/core-accordion/Accordion.vue'
import Icons from '@sephora-asia/atoms-icons/Icons.vue'
import i18n from '../mixins/i18n'

export default {
  name: 'AccordionsContainer',

  components: {
    Accordion,
    Icons
  },

  mixins: [i18n],

  props: {
    brandDescription: {
      type: String,
      default: ''
    },
    entityType: {
      type: String,
      required: true
    },
    paramTopCategory: {
      type: String,
      required: true
    },
    paramCategory: {
      type: String,
      required: true
    },
    paramSubCategory: {
      type: String,
      required: true
    }
  },

  computed: {
    ...mapGetters({
      categoryFilters: 'listingPageFilters/categoryFilters'
    }),
    baseUrl() {
      return [pluralize(this.entityType), this.$route.params.id]
        .filter((item) => !!item)
        .join('/')
    },
    categorySlugUrl() {
      return [this.paramTopCategory, this.paramCategory, this.paramSubCategory]
        .filter((cat) => !!cat)
        .join('/')
    },
    currentCategory() {
      return this.categoryFilters.find(
        (category) => category.slugUrl === this.categorySlugUrl
      )
    },
    subCategories() {
      if (this.currentCategory) {
        // for both brands (with lvl1 category) and categories page
        return this.categoryFilters.filter(
          (category) => category.parentId === this.currentCategory.id
        )
      } else {
        // for brands page (without category)
        return this.categoryFilters.filter(
          (category) => category.parentId == null
        )
      }
    },
    parentCategory() {
      if (this.currentCategory) {
        const category = this.categoryFilters.find(
          (category) => category.id === this.currentCategory.parentId
        )
        if (!category && this.entityType === 'brand') {
          // for brands with lvl1 category page
          return {
            label: this.$t('category.allCategories'),
            slugUrl: ''
          }
        } else {
          // for categories page
          return category
        }
      } else {
        return false
      }
    },
    categoryTitle() {
      let title = this.$t('category.seeAllCategory')

      if (this.categorySlugUrl && this.currentCategory) {
        title = this.$t('category.seeAll', {
          category: this.currentCategory.label
        })
      }

      return title
    }
  },

  methods: {
    getCategorySlugUrl(slugUrl) {
      return `/${this.baseUrl}/${slugUrl}`
    }
  }
}
</script>

<style lang="scss" scoped>
.accordions-container {
  padding-top: 10px;

  /deep/ .accordion {
    &:last-child {
      border-bottom: 1px solid $grey;
    }

    .title {
      font-size: 14px;
      font-weight: 700;
      text-transform: uppercase;
      padding: 10px 16px;
    }

    &.about-the-brand-accordion {
      .content {
        padding: 10px 16px;
      }
    }

    .arrow-icon {
      &.expand {
        transform: rotate(270deg);
        transition: transform 0.3s linear;
      }

      &.collapse {
        transform: rotate(90deg);
        transition: transform 0.3s linear;
      }
    }

    // overwritting the original +/- icon
    .expand:after,
    .collapse:after {
      content: unset;
    }

    .category-link,
    .category-parent-link {
      font-weight: normal;
      font-size: 14px;
      padding: 10px 16px;
      display: block;
      border-top: 1px solid $grey;
    }

    .category-parent-link {
      font-weight: 700;
      text-transform: uppercase;
    }
  }
}
</style>
